/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { When } from "react-if";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  Row,
  Col,
  Tabs,
  Tab,
  Button,
  Loader,
  Label,
  Heading,
  SVGIcon,
  Spacer,
  Text,
  theme,
  Message,
} from "@nordcloud/gnui";
import {
  CloudProviderType,
  Provider,
  useAddDiscoveryRuleMutation,
  useAccountV2LazyQuery,
} from "~/generated/graphql";
import { BreadcrumbsBox, IconBox, InfoCard, Fieldset } from "~/components";
import { ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { useQueryState } from "~/hooks";
import { useAddAccount, useUpdateAccount } from "~/hooks/graphql";
import { useFeature } from "~/hooks/useFeature";
import { ROUTES } from "~/routing/routes";
import { showSuccess, showError } from "~/services/toast";
import { getIcon, isEmpty, isNotEmpty, type Maybe } from "~/tools";
import {
  getInitialAccountParams,
  getInitialSettings,
} from "~/views/accounts/utils";
import { AwsAccountForm } from "./AddAccount/AwsAccountForm";
import { AzureAccountForm } from "./AddAccount/AzureAccountForm";
import {
  PROVIDERS,
  ProviderKeys,
  HybridProviderTypes,
  HYBRID_PROVIDERS,
} from "./AddAccount/constants";
import { GcpAccountForm, GoogleUserInput } from "./AddAccount/GcpAccountForm";
import { HybridAccountForm } from "./AddAccount/HybridAccountForm";
import { IbmcloudAccountForm } from "./AddAccount/IbmcloudAccountForm";
import { getCustomerError } from "./AddAccount/utils";
import { schema } from "./AddAccount/validators";
import { ApplicationInput } from "./components/ApplicationInput";
import { EnvironmentInput } from "./components/EnvironmentInput";
import { AccountContacts, AccountParams, AccountMappingRules } from "./types";

const ProviderIconBox = styled(IconBox)`
  & * {
    filter: grayscale(100%);
    transition: ${theme.transition};
  }
  &:hover * {
    filter: none;
  }
`;

type AccountQuery = {
  accountId: string;
  source: string;
};

export function AccountAddPage() {
  const {
    state: { accountId: selectedAccountId, source },
    set,
  } = useQueryState<AccountQuery>();

  const [addAccount, { loading, error }] = useAddAccount();
  const [updateAccount, { loading: loadingUpdate, error: errorUpdate }] =
    useUpdateAccount();
  const [currentStep, setCurrentStep] = React.useState(0);
  const [accountAdded, setAccountAdded] = React.useState(false);
  const [accountId, setAccountId] = React.useState(selectedAccountId || "");
  const [assignAccount, setAssignAccount] = React.useState(false);
  const [getAccount, { data: accountQuery }] = useAccountV2LazyQuery();

  React.useEffect(() => {
    if (accountId && currentStep === 0) {
      getAccount({
        variables: { id: accountId },
      });
    }
  }, [accountId, currentStep, getAccount]);

  const initialSettings: AccountContacts = React.useMemo(
    () => getInitialSettings(!!accountId, accountQuery),
    [accountId, accountQuery]
  );
  const initialAccountParams = React.useMemo(
    () => getInitialAccountParams(!!accountId, accountQuery),
    [accountId, accountQuery]
  );

  const [additionalSettings, setAdditionalSettings] =
    React.useState<AccountContacts>(initialSettings);
  const [accountParams, setAccountParams] =
    React.useState<AccountParams>(initialAccountParams);
  const [mappingRulesAccount, setMappingRulesAccount] =
    React.useState<AccountMappingRules>({
      appId: "",
      envId: "",
      environments: [],
    });

  const [addDiscoveryRule, { loading: loadingRule }] =
    useAddDiscoveryRuleMutation();

  const navigate = useNavigate();

  const { register, setValue } = useForm({
    resolver: yupResolver(schema),
  });

  React.useEffect(() => {
    register("ownerId");
    register("contactIds");
  }, [register]);

  React.useEffect(() => {
    if (
      isNotEmpty(accountId ?? "") &&
      isEmpty(accountParams.providerType ?? "")
    ) {
      setAccountParams(initialAccountParams);
      setAdditionalSettings(initialSettings);
    }
  }, [
    initialAccountParams,
    initialSettings,
    accountId,
    accountParams.providerType,
  ]);

  const handleStep = (step: number) => {
    setCurrentStep(step);
  };
  const nextStep = () => {
    setCurrentStep((prevStep) => prevStep + 1);
  };
  const previousStep = () => {
    if (accountId) {
      const route = source === "detail" ? accountId : `?tab=${source}`;
      navigate(`${ROUTES.accounts.index}/${route}`);
    }
    setAdditionalSettings(initialSettings);
    setAccountParams(initialAccountParams);
    setCurrentStep((prevStep) => prevStep - 1);
  };

  const onAddDiscoveryRule = async () => {
    try {
      await addDiscoveryRule({
        variables: {
          input: {
            name: `DR${Date.now().toString()}`,
            match: {
              cloudAccount: [accountId],
            },
            environmentId: mappingRulesAccount.envId,
          },
        },
      });
      showSuccess("Account assigned.");
      nextStep();
    } catch {
      showError("Something went wrong");
    }
  };
  const getCredentials = (values: Record<string, unknown>) => {
    const { provider } = accountParams;
    let credentials = values.credentials || values;
    if (provider === "AWS") {
      credentials = {
        roles: [{ arn: values.arn, externalId: values.externalId }],
      };
    }
    if (provider === "IBMCLOUD") {
      credentials = {
        apikey: values.appKey,
      };
    }
    return credentials;
  };

  const getDetectedProvider = (provider: string) =>
    Object.keys(Provider).find(
      (key) => key.toLowerCase() === provider.toLowerCase()
    ) || "Aws";

  const getDetectedProviderType = (providerType?: string) =>
    Object.keys(CloudProviderType).find(
      (key) => key.toLowerCase() === providerType?.toLowerCase()
    );

  const getAccountStatus = (status?: string) =>
    status === "discovered" ? "inactive" : status;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onAddAccountSubmit = async (values: Record<string, any>) => {
    const { provider, providerType } = accountParams;
    const detectedProvider: string = getDetectedProvider(provider);

    const detectedProviderType = getDetectedProviderType(providerType);

    const inputHybrid = {
      provider: Provider[detectedProvider as keyof typeof Provider],
      providerType: detectedProviderType
        ? CloudProviderType[
            detectedProviderType as keyof typeof CloudProviderType
          ]
        : undefined,
      name: values.name || values.clusterName,
      ownerId: additionalSettings.ownerId,
    };

    const isHybrid = provider === Provider.Hybrid;

    const input = isHybrid
      ? inputHybrid
      : {
          ...inputHybrid,
          providerId: values.providerId ?? "",
          name: values.providerId,
          displayName: values.displayName,
          description: values.description,
          contactIds: additionalSettings.contactIds
            ? [additionalSettings.contactIds]
            : null,
          creds: JSON.stringify(getCredentials(values)),
        };

    try {
      let accId: Maybe<string> = null;
      if (isHybrid && isNotEmpty(accountId ?? "")) {
        await updateAccount({
          id: accountId,
          name: values.clusterName,
          ownerId: inputHybrid.ownerId,
          status: getAccountStatus(accountParams.status),
        });
        accId = accountId;
      } else {
        const response = await addAccount({ variables: { input } });
        accId = response?.data?.addAccountV2?.id;
      }

      if (accId == null) {
        showError(ERROR_TEXT.failedAddingAccount, { autoClose: false });
      }
      setAccountId(accId ?? "");
      setAccountAdded(true);
      nextStep();
      showSuccess(SUCCESS_TEXT.accountAdded);
    } catch (accountOperationError) {
      showError(getCustomerError(accountOperationError), {
        autoClose: false,
      });
    }
  };

  const handleGcpFormSubmit = (data: GoogleUserInput) =>
    onAddAccountSubmit({
      ...data,
      name: data.providerId,
      credentials: data.creds,
    });

  const loadingState = loading || loadingUpdate ? "loading" : undefined;
  const errorState = error || errorUpdate ? "error" : undefined;
  const initialButtonState = loadingState ?? errorState;

  const showHybridButton = accountParams.provider === Provider.Hybrid;

  const useExperimental = useFeature("allowExperimental");

  return (
    <Row>
      <Col>
        {(loading || loadingRule) && <Loader />}
        <BreadcrumbsBox title="Add Account" />
        <Tabs wizard step={currentStep} handleTab={handleStep}>
          {!selectedAccountId && (
            <Tab
              disabled
              heading="Select Provider"
              caption=""
              buttonsJustify="flex-end"
            >
              <Row
                justify="center"
                css={{
                  paddingTop: theme.spacing.spacing06,
                  paddingBottom: theme.spacing.spacing06,
                }}
              >
                <Col xs={2} sm={6} md={6} lg={8}>
                  <Row>
                    {Object.values(PROVIDERS).map((provider: ProviderKeys) => (
                      <Col
                        key={provider}
                        xs={2}
                        sm={2}
                        md={2}
                        lg={2.4}
                        css={{ marginBottom: theme.spacing.spacing07 }}
                      >
                        <ProviderIconBox
                          label={provider === "GCP" ? "Google Cloud" : provider}
                          data-testid={provider}
                          style={{
                            padding: theme.spacing.spacing06,
                            border: "1px solid",
                            borderColor:
                              provider === accountParams.provider
                                ? theme.color.border.focus
                                : theme.color.border.border01,
                          }}
                          icon={
                            <SVGIcon
                              name={getIcon(provider.toLocaleLowerCase())}
                              size="xl"
                              css={{
                                borderRadius: "4px",
                              }}
                            />
                          }
                          onClick={() => {
                            setAccountParams({
                              ...accountParams,
                              provider,
                            });
                            provider !== PROVIDERS.hybrid && nextStep();
                          }}
                        />
                      </Col>
                    ))}
                  </Row>
                  <Spacer height="2rem" />

                  <Row>
                    <When
                      condition={accountParams.provider === PROVIDERS.hybrid}
                    >
                      {Object.values(HYBRID_PROVIDERS)
                        .filter((provider) =>
                          provider === HYBRID_PROVIDERS.vmware
                            ? useExperimental
                            : true
                        )
                        .map((hybridProviderType: HybridProviderTypes) => (
                          <Col
                            key={hybridProviderType}
                            xs={2}
                            sm={2}
                            md={2}
                            lg={3}
                          >
                            <ProviderIconBox
                              label={hybridProviderType}
                              data-testid={hybridProviderType}
                              style={{
                                border: "1px solid",
                                borderColor:
                                  hybridProviderType ===
                                  accountParams.providerType
                                    ? theme.color.border.focus
                                    : theme.color.border.border01,
                              }}
                              icon={
                                <SVGIcon
                                  size="xl"
                                  name={getIcon(
                                    hybridProviderType.toLocaleLowerCase()
                                  )}
                                  css={{
                                    borderRadius: `${theme.radiusDefault}`,
                                  }}
                                />
                              }
                              onClick={() => {
                                setAccountParams({
                                  ...accountParams,
                                  providerType: hybridProviderType,
                                });
                                nextStep();
                              }}
                            />
                          </Col>
                        ))}
                    </When>
                  </Row>
                </Col>
              </Row>
            </Tab>
          )}
          <Tab
            disabled
            heading="Account Details"
            caption=""
            buttons={
              <>
                <Button
                  severity="low"
                  icon="arrowLeft"
                  mr={2}
                  onClick={previousStep}
                >
                  Previous Step
                </Button>
                <When condition={accountParams.provider === "AWS"}>
                  <Button
                    initialState={initialButtonState}
                    icon="plus"
                    type="submit"
                    form="AWS-cred-form"
                  >
                    Add AWS Account
                  </Button>
                </When>
                <When condition={accountParams.provider === "AZURE"}>
                  <Button
                    initialState={initialButtonState}
                    icon="plus"
                    type="submit"
                    form="AZURE-cred-form"
                  >
                    Add Azure Account
                  </Button>
                </When>
                <When condition={accountParams.provider === "GCP"}>
                  <Button
                    initialState={initialButtonState}
                    icon="plus"
                    type="submit"
                    form="GCP-cred-form"
                  >
                    Add GCP Account
                  </Button>
                </When>
                <When condition={accountParams.provider === "IBMCLOUD"}>
                  <Button
                    initialState={initialButtonState}
                    icon="plus"
                    type="submit"
                    form="IBMCLOUD-cred-form"
                  >
                    Add IBMCLOUD Account
                  </Button>
                </When>
                <When condition={showHybridButton}>
                  <Button
                    initialState={initialButtonState}
                    icon="plus"
                    type="submit"
                    form="HYBRID-cred-form"
                  >
                    {`Add ${accountParams.providerType} account`}
                  </Button>
                </When>
              </>
            }
          >
            <Row justify="center">
              <Col sm={6} md={6} lg={8}>
                <Row>
                  <Col>
                    <When condition={accountParams.provider === PROVIDERS.aws}>
                      <AwsAccountForm
                        accountParams={accountParams}
                        additionalSettings={additionalSettings}
                        setAdditionalSettings={setAdditionalSettings}
                        onAddAccountSubmit={onAddAccountSubmit}
                      />
                    </When>
                    <When
                      condition={accountParams.provider === PROVIDERS.azure}
                    >
                      <AzureAccountForm
                        accountParams={accountParams}
                        additionalSettings={additionalSettings}
                        setAdditionalSettings={setAdditionalSettings}
                        onAddAccountSubmit={onAddAccountSubmit}
                      />
                    </When>
                    <When condition={accountParams.provider === PROVIDERS.gcp}>
                      <GcpAccountForm
                        id="GCP-cred-form"
                        accountParams={accountParams}
                        additionalSettings={additionalSettings}
                        setAdditionalSettings={setAdditionalSettings}
                        onSubmit={handleGcpFormSubmit}
                      />
                    </When>
                    <When
                      condition={accountParams.provider === PROVIDERS.ibmcloud}
                    >
                      <IbmcloudAccountForm
                        id="IBMCLOUD-cred-form"
                        accountParams={accountParams}
                        additionalSettings={additionalSettings}
                        setAdditionalSettings={setAdditionalSettings}
                        onAddAccountSubmit={onAddAccountSubmit}
                      />
                    </When>
                    <When
                      condition={accountParams.provider === PROVIDERS.hybrid}
                    >
                      <HybridAccountForm
                        accountParams={accountParams}
                        additionalSettings={additionalSettings}
                        setAdditionalSettings={setAdditionalSettings}
                        onAddAccountSubmit={onAddAccountSubmit}
                      />
                    </When>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Tab>
          <Tab
            disabled
            heading="Applications and Environments"
            caption=""
            buttonsJustify="flex-end"
            buttons={
              <Button
                iconRight
                icon="arrowRight"
                disabled={
                  !mappingRulesAccount?.envId ||
                  isEmpty(mappingRulesAccount?.envId)
                }
                onClick={onAddDiscoveryRule}
              >
                Next Step
              </Button>
            }
          >
            <Row justify="center">
              <Col md={6} lg={8}>
                <Row>
                  <Col sm={4}>
                    <InfoCard
                      border={assignAccount ? "red" : "blue"}
                      css={{
                        border: "1px solid",
                        borderColor: assignAccount
                          ? theme.color.border.focus
                          : theme.color.border.border01,
                      }}
                      onClick={() => setAssignAccount(true)}
                    >
                      <Text size="sm" tag="div" color={theme.color.text.text02}>
                        Account resources
                      </Text>
                      <Heading mt=".25rem" mb="1rem" level={3}>
                        Assign Account Resources manually
                      </Heading>
                      <Text color={theme.color.text.text02} size="sm" tag="div">
                        You can manually assign all Account Resources to
                        selected Application and Environment
                      </Text>
                    </InfoCard>
                  </Col>
                  <Col sm={4}>
                    <InfoCard onClick={nextStep}>
                      <Text size="sm" tag="div" color={theme.color.text.text02}>
                        Account resources
                      </Text>
                      <Heading mt=".25rem" mb="1rem" level={3}>
                        Skip manual assigning
                      </Heading>
                      <Text color={theme.color.text.text02} size="sm" tag="div">
                        Account Resources will be assigned automatically to
                        environments and applications if you have defined
                        mapping rules.{" "}
                        <Link to={ROUTES.settings.mapping}>
                          Show global mapping rules
                        </Link>
                      </Text>
                    </InfoCard>
                  </Col>
                </Row>
                <When condition={assignAccount}>
                  <Spacer height="1rem" />
                  <Row>
                    <Col>
                      <Fieldset>
                        <Label required name="Select Application" />
                        <ApplicationInput
                          value={mappingRulesAccount.appId}
                          onChange={(app) => {
                            setMappingRulesAccount({
                              ...mappingRulesAccount,
                              appId: app.id,
                              envId: "",
                              environments: app.environments,
                            });
                          }}
                        />
                      </Fieldset>
                      <Fieldset>
                        <Label required name="Select Environment" />
                        <EnvironmentInput
                          disabled={!mappingRulesAccount.appId}
                          value={mappingRulesAccount.envId}
                          options={mappingRulesAccount.environments}
                          onChange={(envId) => {
                            setMappingRulesAccount({
                              ...mappingRulesAccount,
                              envId,
                            });
                          }}
                        />
                      </Fieldset>
                    </Col>
                  </Row>
                </When>
              </Col>
            </Row>
          </Tab>
          <Tab
            disabled
            heading="Summary"
            caption=""
            buttons={<Spacer height={theme.spacing.spacing08} />}
          >
            <Row justify="center">
              <Col sm={6} md={6} lg={8}>
                <Spacer height="1rem" />
                <When condition={accountAdded}>
                  <Row>
                    <Col>
                      <Message status="success" image="success">
                        Account has been Added
                      </Message>
                    </Col>
                  </Row>
                  <Spacer height="2rem" />
                </When>
                <Row>
                  <Col lg={4}>
                    <InfoCard
                      align="center"
                      onClick={() => {
                        navigate(`${ROUTES.accounts.index}/${accountId}`);
                      }}
                    >
                      <SVGIcon name="detailsView" size="xl" />
                      <Heading marginTop="1rem" level={4}>
                        Account Details
                      </Heading>
                    </InfoCard>
                  </Col>
                  <Col lg={4}>
                    <InfoCard
                      align="center"
                      onClick={() => navigate(`${ROUTES.accounts.index}`)}
                    >
                      <SVGIcon name="listView" size="xl" />
                      <Heading marginTop="1rem" level={4}>
                        List of Accounts
                      </Heading>
                    </InfoCard>
                  </Col>
                  <Col lg={4}>
                    <InfoCard
                      align="center"
                      onClick={() => {
                        setCurrentStep(0);
                        setAccountParams({ provider: "" });
                        setAdditionalSettings({
                          ...additionalSettings,
                          ownerId: "",
                        });
                        setValue("ownerId", "");
                        setAccountAdded(false);
                        setAccountId("");
                        set("accountId")(undefined);
                      }}
                    >
                      <SVGIcon name="wizardView" size="xl" />
                      <Heading marginTop="1rem" level={4}>
                        Add Another Account
                      </Heading>
                    </InfoCard>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Tab>
        </Tabs>
      </Col>
    </Row>
  );
}
